import { useForm } from '@shared/composables/useForm';
import { useRoute } from '@shared/composables/useRoute';
import { usePayload } from '@shared/composables/usePayload';
import { useInjectErrorState } from '@shared/composables/useProvideErrorState';
import { useWithValidationConfig } from '@shared/composables/useWithValidationConfig';
import { type ISteward, Steward } from '@app/land-hold/steward/models/Steward';
import type { FormOptions } from '@shared/types/Form';
import { useFetchListData } from '@shared/composables/useFetchListData.ts';

export function useStewardRepository<T extends ISteward = ISteward>() {
    const command = () => {
        const store = (data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                Steward.routes().store,
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const update = (id: string, data: T, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<T>(data).precognition(
                {
                    url: useRoute().build(Steward.routes().update, {
                        id,
                    }),
                    method: Steward.routes().update.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.transform((data) => usePayload<T>(data)).submit(options);
            }

            return { form, execute };
        };

        const destroy = <U extends { id: string }>(id: string, options?: FormOptions) => {
            const hasError = useInjectErrorState();
            const form = useForm<U>({ id } as U).precognition(
                {
                    url: useRoute().build(Steward.routes().destroy, {
                        id,
                    }),
                    method: Steward.routes().destroy.method,
                },
                useWithValidationConfig(hasError, () => form, options),
            );
            function execute(options?: FormOptions) {
                form.submit(options);
            }

            return { form, execute };
        };

        return {
            store,
            update,
            destroy,
        };
    };

    const query = () => {
        const buildGetUrl = (id: string) => {
            return { endpoint: useRoute().build(Steward.routes().show, { id }) };
        };

        const list = () => {
            const { data: stewards, processing: fetchingStewards } = useFetchListData<Steward>(useRoute().build(Steward.routes().index), 'appSection@steward::IndexStewardsPage', (payload) => Steward.from(payload));
            return { stewards, fetchingStewards };
        };

        return {
            buildGetUrl,
            list,
        };
    };

    return {
        command: command(),
        query: query(),
    };
}
